import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { ScrollArea } from "../../components/ui/scroll-area";
import { Textarea } from "../../components/ui/textarea";
import { LimitsTab } from "../../components/requirementss/LimitsTab";
import { DisbursementTab } from "../../components/requirementss/DisbursementTab";
import { AddLenderDialog, AddLenderValues } from "../../components/requirements/AddLenderDialog";
import { LenderStatusDialog, StatusFormValues } from "../../components/requirementss/LenderStatusDialog";
import { StatusTimeline } from "../../components/requirementss/StatusTimeline";
import { RequirementForm, RequirementFormValues } from "../../components/requirements/RequirementForm";
import { loadState, saveState } from "../../utils/storage";
import { DealDeskState, Requirement, LenderTag, StatusHistoryEntry, DisbursementTranche, FilterState } from "../../types/deals";
import { getStatusConfig } from "../../utils/statusConfig";
import { parseIdbUrl } from "../../utils/idbFiles";
import { useIdbObjectUrl } from "../../hooks/use-idb-object-url";
import { 
  Building2, 
  IndianRupee, 
  Wallet, 
  Banknote, 
  CalendarDays, 
  FileText, 
  UserRound, 
  Briefcase,
  Clock,
  ShieldCheck,
  TrendingUp,
  Plus,
  Edit2,
  RefreshCw,
  Upload,
  Eye,
  Trash2,
  Link,
  Package,
  ArrowLeft,
} from "lucide-react";
import apiService from "../../services/apiService";
import { toast } from "react-toastify";
import { useCustomerWorkflow } from "../../context/CustomerWorkflowContext";

const initialFilters: FilterState = {
  search: "",
  products: [],
  statuses: [],
  applicationRMs: [],
  dateRange: {},
  lenderInstitutions: [],
  lenderNames: [],
  requirementTypes: [],
  customerType: [],
  showStagnant: false,
  currentPageFilter: "loggedIn",
};

const initial: DealDeskState = { 
  requirements: [],
  filters: initialFilters
};

interface AdminUserList {
  id: number;
  name: string;
}

interface LenderCompanyList {
  id: number;
  name: string;
}

interface LenderTypes {
  id: number;
  name: string;
  display_order: number;
  parent_type: string;
}

interface AssetCategory {
  id: number;
  name: string;
}

function enrichRequirementDisplay(
  req: Requirement,
  productTypes: LenderTypes[],
  rmList: AdminUserList[],
  assetCategories: AssetCategory[]
): Requirement {
  return {
    ...req,
    productTypeName:
      req.productTypeName ??
      productTypes.find((p) => p.id === req.product_type_id)?.name,
    applicationRM:
      req.applicationRM ??
      rmList.find((u) => String(u.id) === String(req.applicationRMId))?.name,
    assetCategoryName:
      req.assetCategoryName ??
      assetCategories.find((c) => c.id === req.asset_category_id)?.name,
  };
}


export default function RequirementDetail({
  requirementData,
  adminUserList,
  onBack,
}: {
  requirementData: any;
  adminUserList: AdminUserList[];
  onBack?: () => void;
}) {
  const { refreshWorkflow } = useCustomerWorkflow();
  const navigate = useNavigate();
  const [lenderCompanyList, setLenderCompanyList] = useState<LenderCompanyList[]>([]);
  const [lenderUserProductTypes, setLenderUserProductTypes] = useState<LenderTypes[]>([]);
  const [assetCategories, setAssetCategories] = useState<AssetCategory[]>([]);
  const [LenderStatus, setLenderStatus] = useState<any>();
  const [disbursements, setDisbursements] = useState<DisbursementTranche[]>([]);
  const [isDisbursementsLoading, setIsDisbursementsLoading] = useState(false);

  const requirementId = requirementData?.id ?? requirementData?.requirementId ?? requirementData?.requirement_id;

  const fetchRequirementById = async (id: string): Promise<Requirement | null> => {
    try {
      const res: any = await apiService.get<any>(`/login/get-requirement/${id}`);
      const data = res?.data ?? res?.result ?? res;
      return data as Requirement;
    } catch (e) {
      console.error("Error fetching requirement:", e);
      return null;
    }
  };

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!requirementId) return;
      const data = await fetchRequirementById(requirementId);
      if (!cancelled && data) {
        setRequirement(data);
        setState((s) => ({
          ...s,
          requirements: s.requirements.map((r) => (String(r.id) === String(requirementId) ? data : r)),
        }));
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [requirementId]);

  useEffect(() => {
    let cancelled = false;
    async function fetchDisbursedData() {
      const requirementId = requirementData?.id ?? requirementData?.requirementId ?? requirementData?.requirement_id;
      if (!requirementId) return;
      setIsDisbursementsLoading(true);
      try {
        const res: any = await apiService.get<any>(`/login/get-disbursed-data/${requirementId}`);
        const list =
          (res?.data ?? res?.disbursements ?? res?.result ?? res) as
            | DisbursementTranche[]
            | undefined;
        if (!cancelled) setDisbursements(Array.isArray(list) ? list : []);
      } catch (e) {
        if (!cancelled) setDisbursements([]);
      } finally {
        if (!cancelled) setIsDisbursementsLoading(false);
      }
    }
    fetchDisbursedData();
    return () => {
      cancelled = true;
    };
  }, [requirementData?.id, requirementData?.requirementId, requirementData?.requirement_id]);



   // fetch all data on mount
   useEffect(() => {
    const fetchData = async () => {
      // setLoading(true);
      try {
        const [productRes, statusRes, assetCategoryRes] = await Promise.all([
          apiService.get<LenderTypes[]>("/lender/get-lender-user-product-types"),
          apiService.get<any>("/login/get-lender-status"),
          apiService.get<{ data: AssetCategory[] }>("/lender/get-asset-categories-list"),
        ]);
        fetchLenderCompanyList();
        setLenderUserProductTypes((productRes as any)?.data || productRes || []);
        setLenderStatus(statusRes?.data || []);
        setAssetCategories(Array.isArray(assetCategoryRes?.data) ? assetCategoryRes.data : []);
      } catch (err) {
        console.error("Error fetching data:", err);
      } finally {
        // setLoading(false);
      }
    };

    fetchData();
  }, []);

  const fetchLenderCompanyList = async () => {
    try {
      const response = await apiService.get<{ data: LenderCompanyList[] }>(`/customers/get-lender-company-list`);
      const transformedDocs = response.data.map((doc) => ({
        name: doc.name,
        id: doc.id
      }));
      setLenderCompanyList(transformedDocs);
    } catch (error) {
      console.error("Error fetching lender companies:", error);
    }
  };

  
  const [state, setState] = useState<DealDeskState>(() => loadState<DealDeskState>(initial));
  const [selectedLenderId, setSelectedLenderId] = useState<string | null>(null);
  const [addLenderOpen, setAddLenderOpen] = useState(false);
  const [statusOpenFor, setStatusOpenFor] = useState<string | null>(null);
  const [editReqOpen, setEditReqOpen] = useState(false);
  const [isUpdatingRequirement, setIsUpdatingRequirement] = useState(false);

  const [requirement, setRequirement] = useState<Requirement | null>(requirementData ?? null);

  const displayRequirement = useMemo(() => {
    if (!requirement) return null;
    return enrichRequirementDisplay(requirement, lenderUserProductTypes, adminUserList, assetCategories);
  }, [requirement, lenderUserProductTypes, adminUserList, assetCategories]);

  const [sanctionLetterUrl, setSanctionLetterUrl] = useState<string>("");
  const [uploadingDocType, setUploadingDocType] = useState<string | null>(null);
  const selectedLender = useMemo(() => requirement?.lenders.find(l => l.id === selectedLenderId) ?? null, [requirement, selectedLenderId]);
  const [collateralDraft, setCollateralDraft] = useState("");
  const [isSavingCollateral, setIsSavingCollateral] = useState(false);

  const canEditRequirement = requirement?.lenders.length === 0;

  const selectedLenderSanctionEntry = useMemo(() => {
    if (!selectedLender) return null;
    return [...selectedLender.statusHistory]
      .reverse()
      .find((e) => e.sanctionDate || e.collateralDetails || e.sanctionLetterUrl || e.sanctionLetterName) ?? null;
  }, [selectedLender]);

  // Sync collateral editor with selected lender
  useEffect(() => {
    setCollateralDraft(String(selectedLenderSanctionEntry?.collateralDetails ?? ""));
  }, [selectedLenderSanctionEntry?.collateralDetails, selectedLenderId]);

  async function saveCollateralDetails(nextText: string) {
    if (!requirement?.id || !selectedLender?.id) return;
    setIsSavingCollateral(true);
    try {
      await apiService.post("/login/update-collateral-details", {
        lenderId: selectedLender.id,
        requirementId: requirement.id,
        collateralDetails: nextText,
      });

      // patch local state (update latest sanction/disbursed entry)
      const nextLenders = requirement.lenders.map((l: any) => {
        if (l.id !== selectedLender.id) return l;
        const idx = [...l.statusHistory].map((e: any) => e).reverse().findIndex((e: any) => String(e.statusId) === "8" || String(e.statusId) === "9");
        if (idx < 0) return l;
        const realIdx = l.statusHistory.length - 1 - idx;
        const nextHistory = [...l.statusHistory];
        nextHistory[realIdx] = { ...nextHistory[realIdx], collateralDetails: nextText };
        return { ...l, statusHistory: nextHistory };
      });
      const nextReq = { ...requirement, lenders: nextLenders } as Requirement;
      setRequirement(nextReq);
      updateRequirement(nextReq);
      toast.success("Collateral details updated");
    } catch (e) {
      console.error("Failed to update collateral details", e);
      toast.error("Failed to update collateral details");
    } finally {
      setIsSavingCollateral(false);
    }
  }

  const selectedLenderSanctionLetter = useMemo(() => {
    if (!selectedLenderSanctionEntry?.sanctionLetterUrl) return null;
    return {
      name: selectedLenderSanctionEntry.sanctionLetterName || "Sanction Letter",
      url: selectedLenderSanctionEntry.sanctionLetterUrl,
    };
  }, [selectedLenderSanctionEntry]);

  const selectedLenderSanctionLetterKey = useMemo(
    () => parseIdbUrl(selectedLenderSanctionLetter?.url),
    [selectedLenderSanctionLetter?.url]
  );

  const { url: selectedLenderSanctionLetterViewUrl } = useIdbObjectUrl(selectedLenderSanctionLetterKey);
  const selectedLenderSanctionLetterResolvedUrl =
    selectedLenderSanctionLetterViewUrl ?? (selectedLenderSanctionLetter?.url ?? null);

  // Keep simple URL state in sync so the UI block shows correctly
  useEffect(() => {
    setSanctionLetterUrl(selectedLenderSanctionLetterResolvedUrl ?? "");
  }, [selectedLenderSanctionLetterResolvedUrl]);

  // Auto-select first lender when requirement loads
  useEffect(() => {
    if (requirement && requirement.lenders.length > 0 && !selectedLenderId) {
      setSelectedLenderId(requirement.lenders[0].id || "");
    }
  }, [requirement, selectedLenderId]);

  useEffect(() => { saveState(state); }, [state]);

  if (!requirement) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="p-8 text-center">
          <h2 className="text-xl font-semibold mb-2">Requirement Not Found</h2>
          <p className="text-muted-foreground mb-4">The requirement you're looking for doesn't exist.</p>
          <Button onClick={() => navigate("/")}>Back to Dashboard</Button>
        </Card>
      </div>
    );
  }

  function updateRequirement(updated: Requirement) {
    setState((s) => ({ ...s, requirements: s.requirements.map((r) => r.id === updated.id ? updated : r) }));
  }

  async function handleUpdateRequirement(values: RequirementFormValues) {
    if (!requirement?.id) return;
    const updatedReq: Requirement = {
      ...requirement,
      type: values.type,
      product_type_id: Number(values.products),
      asset_category_id: Number(values.assetCategory),
      quantum: values.quantum,
      tenure: values.tenure,
      applicationRM: String(adminUserList.find((user) => user.id === Number(values.applicationRM))?.name ?? requirement.applicationRM),
      applicationRMId: values.applicationRM,
      dealNote: values.dealNote,
      productTypeName:
        lenderUserProductTypes.find((p) => p.id === Number(values.products))?.name ?? requirement.productTypeName,
      assetCategoryName:
        assetCategories.find((c) => c.id === Number(values.assetCategory))?.name ?? requirement.assetCategoryName,
    };
    setIsUpdatingRequirement(true);
    try {
      await apiService.post<{ data?: Requirement }>('/login/update-requirements', {
        requirementId: requirement.id,
        updates: updatedReq,
      });
      const refreshed = await fetchRequirementById(requirement.id);
      const result = enrichRequirementDisplay(
        refreshed ?? updatedReq,
        lenderUserProductTypes,
        adminUserList,
        assetCategories
      );
      setRequirement(result);
      updateRequirement(result);
      setEditReqOpen(false);
      toast.success("Requirement updated");
    } catch {
      toast.error("Failed to update requirement");
    } finally {
      setIsUpdatingRequirement(false);
    }
  }

  async function handleAddLender(values: AddLenderValues) {
    
    const now = new Date().toISOString();
    const institutionName = lenderCompanyList.find((i) => String(i.id) === String(values.institution))?.name ?? "";
    const institutionId = lenderCompanyList.find((i) => String(i.id) === String(values.institution))?.id ?? "";
    const newTag: LenderTag = {
      lenderId: values.lenderName,
      requirementId: requirement?.id || "",
      institutionId: institutionId.toString(),
      institutionName: institutionName.toString(),
      name: values.lenderName,
      lenderName: values.lenderName,
      programLimit: values.programLimit,
      applicationRM: values.applicationRM,
      note: values.note,
      taggedAt: now,
      customerId: requirement?.customerId || "",
      statusHistory: [{ statusId: 1, timestamp: now, updatedBy: String(requirement?.applicationRM) }],
      comments: []
    };

    if (!requirement) return; // or handle differently

    interface TagResponse {
      id: string;
      data: any;
    }

    try {
      await apiService.post<TagResponse>('/login/tag-lender-proposal', {
        newTag
      });
      const refreshed = await fetchRequirementById(requirement.id!);
      if (refreshed) {
        const enriched = enrichRequirementDisplay(
          refreshed,
          lenderUserProductTypes,
          adminUserList,
          assetCategories
        );
        setRequirement(enriched);
        setState((s) => ({
          ...s,
          requirements: s.requirements.map((r) => (String(r.id) === String(requirement.id) ? enriched : r)),
        }));
      }
      setAddLenderOpen(false);
    } catch (error:any) {
      console.error("Error adding lender tag: " + error.message);
      toast.error(error.message || "Failed to add lender");
    }finally{
      await refreshWorkflow();
    }
  }

  async function handleStatusUpdate(lenderId: string, vals: StatusFormValues) {
    const now = new Date().toISOString();
    let sanctionLetterUrlFromApi: string | undefined;

    // If sanction letter file is uploaded, call upload API first to get persistent URL
    const sanctionFile = vals.sanctionLetterFile as File | undefined;
    if (sanctionFile && sanctionFile.size > 0) {
      try {
        const formData = new FormData();
        formData.append("file", sanctionFile);
        const response = await apiService.post<{ linkUrl?: string }>(
          `/login/upload-sanction-letter/${lenderId}`,
          formData,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
        sanctionLetterUrlFromApi = response?.linkUrl ?? (response as any)?.data?.linkUrl;
      } catch (e) {
        console.error("Sanction letter upload failed", e);
        toast.error("Failed to upload sanction letter");
      }
    }

    const updated = requirement?.lenders?.map((l: any) => {
      if (l.id !== lenderId) return l;
      const entry: StatusHistoryEntry = {
        status: LenderStatus?.find((s: any) => String(s.id) === String(vals.status))?.name || "",
        statusId : LenderStatus?.find((s: any) => String(s.id) === String(vals.status))?.id,
        timestamp: now,
        updatedBy: requirement?.applicationRM || "",
        note: vals.note,
        reason: vals.reason,
      };
      if (vals.termSheetFile) {
        const file = vals.termSheetFile as File;
        entry.termSheetName = file.name;
        entry.termSheetUrl = URL.createObjectURL(file);
      }
      if (typeof vals.sanctionAmount === "number") {
        entry.sanctionAmount = vals.sanctionAmount;
      }
      const sanctionDate = (vals as any).sanctionDate;
      if (sanctionDate) {
        entry.sanctionDate = sanctionDate instanceof Date
          ? sanctionDate.toISOString()
          : sanctionDate;
      }
      const sanctionValidTill = (vals as any).sanctionValidTill;
      if (sanctionValidTill) {
        entry.sanctionValidTill = sanctionValidTill instanceof Date
          ? sanctionValidTill.toISOString()
          : sanctionValidTill;
      }
      if (sanctionLetterUrlFromApi && sanctionFile) {
        entry.sanctionLetterName = sanctionFile.name;
        entry.sanctionLetterUrl = sanctionLetterUrlFromApi;
      } else if (vals.sanctionLetterFile && !sanctionLetterUrlFromApi) {
        const file = vals.sanctionLetterFile as File;
        entry.sanctionLetterName = file.name;
        entry.sanctionLetterUrl = URL.createObjectURL(file);
      }
      if (typeof vals.disbursedAmount === "number") {
        entry.disbursedAmount = vals.disbursedAmount;
        entry.payoutBasis = vals.payoutBasis;
        
        if (vals.payoutBasis === 'manual') {
          entry.customerPayout = vals.customerPayout;
          entry.lenderPayout = vals.lenderPayout;
          entry.partnerPayout = vals.partnerPayout;
        } else {
          const baseAmount = vals.payoutBasis === "sanctioned" 
            ? (entry.sanctionAmount ?? 0)
            : vals.disbursedAmount;
          
          entry.customerPayoutPercent = vals.customerPayoutPercent;
          entry.lenderPayoutPercent = vals.lenderPayoutPercent;
          entry.partnerPayoutPercent = vals.partnerPayoutPercent;
          
          entry.customerPayout = ((vals.customerPayoutPercent ?? 0) / 100) * baseAmount;
          entry.lenderPayout = ((vals.lenderPayoutPercent ?? 0) / 100) * baseAmount;
          entry.partnerPayout = ((vals.partnerPayoutPercent ?? 0) / 100) * baseAmount;
        }
        
        const rev = (entry.customerPayout ?? 0) + (entry.lenderPayout ?? 0) - (entry.partnerPayout ?? 0);
        entry.totalRevenue = rev;
      }
      return { ...l, statusHistory: [...l.statusHistory, entry] };
    });

    const payload = { ...vals } as StatusFormValues & { sanctionLetterUrl?: string };
    if (sanctionLetterUrlFromApi) payload.sanctionLetterUrl = sanctionLetterUrlFromApi;
    await apiService.post('/login/lender-status-update', { lenderId, vals: payload });
    await refreshWorkflow();
    const nextRequirement = { ...requirement, lenders: updated } as Requirement;
    updateRequirement(nextRequirement);
    setRequirement(nextRequirement);
    setStatusOpenFor(null);
  }

  const handleViewAttachment = (url: string) => window.open(url, "_blank");

  async function handleSanctionLetterUpload(lenderId: string, file: File) {
    if (!requirement) return;

    setUploadingDocType("sanctionLetter");

    // If no file (or placeholder empty file) -> clear existing sanction letter locally, do NOT call API
    if (!file || file.size === 0) {
      const updatedLenders = requirement.lenders.map((l: any) => {
        if (l.id !== lenderId) return l;
        if (!l.statusHistory || l.statusHistory.length === 0) return l;

        const lastIdx = l.statusHistory.length - 1;
        const last = l.statusHistory[lastIdx];
        const patched: StatusHistoryEntry = {
          ...last,
          sanctionLetterName: undefined,
          sanctionLetterUrl: "",
        };
        const nextHistory = [...l.statusHistory];
        nextHistory[lastIdx] = patched;
        return { ...l, statusHistory: nextHistory };
      });

      const nextRequirement: Requirement = { ...requirement, lenders: updatedLenders };
      setRequirement(nextRequirement);
      // updateRequirement(nextRequirement);
      setUploadingDocType(null);
      return;
    }

    try {
      const formData = new FormData();
      formData.append("file", file);

      // Find latest statusHistory entry for this lender to get its id

      const updatedLenders = requirement.lenders.map((l: any) => {
        if (l.id !== lenderId) return l;
        if (!l.statusHistory || l.statusHistory.length === 0) return l;

        const lastIdx = l.statusHistory.length - 1;
        const last = l.statusHistory[lastIdx];

        const patched: StatusHistoryEntry = {
          ...last,
          sanctionLetterName: file.name,
          sanctionLetterUrl: "", // temp, will set real link after API response
        };
        const nextHistory = [...l.statusHistory];
        nextHistory[lastIdx] = patched;
        return { ...l, statusHistory: nextHistory };
      });

      const response = await apiService.post<{ linkUrl?: string }>(
        `/login/upload-sanction-letter/${lenderId}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      const linkUrl = response?.linkUrl ?? (response as any)?.data?.linkUrl;

      // Update local requirement state with API-returned linkUrl
      const finalLenders = updatedLenders.map((l: any) => {
        if (l.id !== lenderId) return l;
        if (!l.statusHistory || l.statusHistory.length === 0) return l;

        const lastIdx = l.statusHistory.length - 1;
        const last = l.statusHistory[lastIdx];
        const patched: StatusHistoryEntry = {
          ...last,
          sanctionLetterUrl: linkUrl ?? "",
        };
        const nextHistory = [...l.statusHistory];
        nextHistory[lastIdx] = patched;
        return { ...l, statusHistory: nextHistory };
      });

      const nextRequirement: Requirement = { ...requirement, lenders: finalLenders };
      setRequirement(nextRequirement);
      setSanctionLetterUrl(linkUrl ?? "");

      toast.success("Sanction letter updated");
    } catch (error) {
      console.error("Failed to upload sanction letter", 
        
      );
      toast.error("Failed to upload sanction letter");
    } finally {
      setUploadingDocType(null);
    }
  }

  const handleRemoveAttachment = async (lenderId: string, type: string) => {
    // Simple confirmation before deleting
    const confirmed = window.confirm("Are you sure you want to delete this attachment?");
    if (!confirmed) return;

    if (!sanctionLetterUrl) return;

    try {
      // call backend to remove attachment for this invoice
      // adjust endpoint/path if your API differs

      await apiService.post(`/login/delete-sanction-letter/${lenderId}`, {
        type: type,
      });

      setSanctionLetterUrl("");

      // update local UI

      toast.success("Attachment removed successfully");

      // // refresh related data so parent UI reflects change
      // if (typeof addPayment === "function") addPayment();
      // if (typeof fetchInvoicesAssets === "function") await fetchInvoicesAssets();
    } catch (err) {
      console.error("Failed to remove attachment", err);
      toast.error("Failed to remove attachment");
    }
  };  

  function handleAddDisbursement(disbursementData: Omit<DisbursementTranche, "id">) {
    const newDisbursement: DisbursementTranche = {
      ...disbursementData,
      id: Math.random().toString(36).slice(2, 8).toUpperCase(),
    };
    setDisbursements((prev) => [...prev, newDisbursement]);
  }

  // Calculate summary
  const summary = useMemo(() => {
    let sanction = 0;
    let disbursed = 0;
    const payout = { customer: 0, lender: 0, partner: 0 };
    let latestStatus: string | undefined = undefined;
    
    requirement?.lenders?.forEach((l: any) => {
      latestStatus = l.statusHistory[l.statusHistory.length - 1]?.status || latestStatus;
      const sanc = [...l.statusHistory].reverse().find((e) => typeof e.sanctionAmount === 'number')?.sanctionAmount;
      if (typeof sanc === 'number') sanction += sanc;
      const disbEntry = [...l.statusHistory].reverse().find((e) => typeof e.disbursedAmount === 'number');
      if (disbEntry) {
        disbursed += disbEntry.disbursedAmount ?? 0;
        payout.customer += disbEntry.customerPayout ?? 0;
        payout.lender += disbEntry.lenderPayout ?? 0;
        payout.partner += disbEntry.partnerPayout ?? 0;
      }
    });
    
    const docs = requirement?.lenders?.flatMap((l: any) => l.statusHistory.flatMap((e: any) => {
      const out: { label: string; name: string; url?: string }[] = [];
      if (e.termSheetName) out.push({ label: 'Term Sheet', name: e.termSheetName, url: e.termSheetUrl });
      if (e.sanctionLetterName) out.push({ label: 'Sanction Letter', name: e.sanctionLetterName, url: e.sanctionLetterUrl });
      return out;
    }));
    
    return { sanction, disbursed, payout, latestStatus, docs };
  }, [requirement]);

  // Calculate counts for tab badges
  const sanctionedLendersCount = requirement?.lenders?.filter((l: any) =>
    l.statusHistory.some(
      (e: any) =>
        (e.status === "Sanctioned" || e.status === "Disbursed") &&
        typeof e.sanctionAmount === "number" &&
        e.sanctionAmount > 0
    )
  ).length;
  const disbursementsCount = disbursements.length || 0;

  // Get lender financial details
  const getLenderFinancials = (lender: LenderTag) => {
    const lastSanction = [...lender.statusHistory].reverse().find(e => typeof e.sanctionAmount === 'number')?.sanctionAmount;
    const lastDisbursedEntry = [...lender.statusHistory].reverse().find(e => typeof e.disbursedAmount === 'number');
    const lastDisbursed = lastDisbursedEntry?.disbursedAmount;
    const latestStatus = lender.statusHistory[lender.statusHistory.length - 1]?.status;
    const latestStatusId = lender.statusHistory[lender.statusHistory.length - 1]?.statusId;

    
    return {
      sanctioned: lastSanction,
      disbursed: lastDisbursed,
      status: latestStatus,
      statusId: latestStatusId,
      customerPayout: lastDisbursedEntry?.customerPayout,
      lenderPayout: lastDisbursedEntry?.lenderPayout,
      partnerPayout: lastDisbursedEntry?.partnerPayout,
      totalRevenue: lastDisbursedEntry?.totalRevenue,
    };
  };

  const lenderStatus = useMemo(() => {
    if (requirement?.lenders?.length === 0) return "No Lenders";
    const allStatuses = requirement?.lenders?.map((l: any) => l.statusHistory[l.statusHistory.length - 1]?.statusId).filter(Boolean);
    if (allStatuses?.some((s: any) => String(s) === "9" || s === 9 )) return "Disbursed";
    if (allStatuses?.some((s: any) => String(s) === "8" || s === 8) && !allStatuses?.some((s: any)   => String(s) === "9" || s === 9)) return "Sanctioned";
    if (allStatuses?.some((s: any) => String(s) === "6" || s === 6)) return "Term Sheet Shared";
    if (allStatuses?.every((s: any) => String(s) === "7" || s === 7)) return "Rejected";
    if (allStatuses?.some((s: any) => String(s) === "5" || s === 5)) return "On Hold";
    if (allStatuses?.some((s: any) => String(s) === "4" || s === 4)) return "Unresponsive";
    if (allStatuses?.some((s: any) => String(s) === "3" || s === 3)) return "Under Review";
    if (allStatuses?.some((s: any) => String(s) === "2" || s === 2)) return "Credit Discussion Completed";
    if (allStatuses?.some((s: any) => String(s) === "1" || s === 1)) return "Deal Shared";
    return allStatuses?.[0] || "Deal Shared";
  }, [requirement?.lenders]);

  const handleBack = () => {
    if (onBack) {
      onBack();
      return;
    }
    navigate(-1);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Requirement Header */}
      <div className="container py-6">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={handleBack}
          className="mb-3 h-8 w-fit px-2 -ml-2 text-[#3f8b98] hover:text-[#367a85] hover:bg-[#3f8b981a]"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back
        </Button>
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold">Requirement #{requirement?.requirementId}</h1>
              <Badge variant="secondary">{requirement?.type}</Badge>
                {lenderStatus && (() => {
                  const statusConfig = getStatusConfig(lenderStatus as any);
                  const StatusIcon = statusConfig?.icon;
                 
                  return (
<div className="flex items-center gap-2 shrink-0">
{StatusIcon && <StatusIcon className={`h-3.5 w-3.5 ${statusConfig?.color || ''}`} />}
<Badge variant={statusConfig?.variant || 'secondary'} className="text-xs">
  {lenderStatus || 'Deal Shared'}
</Badge>
</div>

                  );
                })()}
            </div>
            <p className="text-muted-foreground mt-1">
              {requirement?.customer?.name || "Customer"} • Created {new Date(requirement?.createdAt || "").toLocaleDateString("en-GB")}
            </p>
          </div>
          <div className="flex gap-2">
            {(() => {
              const addLenderDisabled = String(requirement?.status) !== "5";
              return (
                <span
                  className={addLenderDisabled ? "inline-block cursor-not-allowed" : ""}
                  title={addLenderDisabled ? "You can add lenders only when status is CC Approved" : undefined}
                >
                  <Button
                    variant="outline"
                    disabled={addLenderDisabled}
                    onClick={() => setAddLenderOpen(true)}
                    className={addLenderDisabled ? "pointer-events-none bg-muted text-muted-foreground opacity-70" : "cursor-pointer bg-primary text-primary-foreground hover:bg-primary/90"}
                    title={!addLenderDisabled ? "Add a new lender" : undefined}
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add Lender
                  </Button>
                </span>
              );
            })()}
            {(() => {
              const editDisabled = !canEditRequirement;
              return (
                <span
                  className={editDisabled ? "inline-block cursor-not-allowed" : ""}
                  title={editDisabled ? "Editing is locked once lenders are tagged" : undefined}
                >
                  <Button
                    variant="outline"
                    disabled={editDisabled}
                    onClick={() => setEditReqOpen(true)}
                    className={editDisabled ? "pointer-events-none bg-muted text-muted-foreground opacity-70" : "cursor-pointer"}
                    title={!editDisabled ? "Edit requirement" : undefined}
                  >
                    <Edit2 className="h-4 w-4 mr-1" />
                    Edit Requirement
                  </Button>
                </span>
              );
            })()}
          </div>
        </div>

        {/* Requirement Summary Cards - Different for Hybrid vs Leasing */}
        {requirement?.type === "Hybrid" ? (
          // Hybrid KPIs: Requested, Sanctioned, Disbursed, Total Revenue
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <Card className="bg-gradient-to-br from-primary/10 to-transparent">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-xs text-muted-foreground">Requested</div>
                    <div className="text-xl font-semibold">₹{new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(requirement?.quantum || 0)}</div>
                  </div>
                  <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <IndianRupee className="h-5 w-5 text-primary" />
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-blue-500/10 to-transparent">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-xs text-muted-foreground">Total Sanctioned</div>
                    <div className="text-xl font-semibold">₹{new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(summary.sanction)}</div>
                  </div>
                  <div className="h-10 w-10 rounded-full bg-blue-500/10 flex items-center justify-center">
                    <ShieldCheck className="h-5 w-5 text-blue-500" />
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-green-500/10 to-transparent">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-xs text-muted-foreground">Disbursed</div>
                    <div className="text-xl font-semibold">₹{new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(summary.disbursed)}</div>
                  </div>
                  <div className="h-10 w-10 rounded-full bg-green-500/10 flex items-center justify-center">
                    <Banknote className="h-5 w-5 text-green-500" />
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-amber-500/10 to-transparent">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-xs text-muted-foreground">Total Revenue</div>
                    <div className="text-xl font-semibold">₹{new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(summary.payout.customer + summary.payout.lender - summary.payout.partner)}</div>
                  </div>
                  <div className="h-10 w-10 rounded-full bg-amber-500/10 flex items-center justify-center">
                    <TrendingUp className="h-5 w-5 text-amber-500" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        ) : (
          // Leasing/Smart EPP KPIs: Requested, Sanctioned, Utilised, Available
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <Card className="bg-gradient-to-br from-primary/10 to-transparent">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-xs text-muted-foreground">Requested</div>
                    <div className="text-xl font-semibold">₹{new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(requirement?.quantum || 0)}</div>
                  </div>
                  <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <IndianRupee className="h-5 w-5 text-primary" />
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-blue-500/10 to-transparent">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-xs text-muted-foreground">Total Sanctioned</div>
                    <div className="text-xl font-semibold">₹{new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(summary.sanction)}</div>
                  </div>
                  <div className="h-10 w-10 rounded-full bg-blue-500/10 flex items-center justify-center">
                    <ShieldCheck className="h-5 w-5 text-blue-500" />
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-amber-500/10 to-transparent">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-xs text-muted-foreground">Total Utilised</div>
                    <div className="text-xl font-semibold">₹{new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format((disbursements || []).reduce((sum: any, d: any) => sum + (d.disbursedAmount || 0), 0))}</div>
                  </div>
                  <div className="h-10 w-10 rounded-full bg-amber-500/10 flex items-center justify-center">
                    <Banknote className="h-5 w-5 text-amber-500" />
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-green-500/10 to-transparent">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-xs text-muted-foreground">Total Available</div>
                    <div className="text-xl font-semibold">₹{new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(Math.max(0, summary.sanction - (disbursements || []).reduce((sum: any, d: any) => sum + (d.disbursedAmount || 0), 0)))}</div>
                  </div>
                  <div className="h-10 w-10 rounded-full bg-green-500/10 flex items-center justify-center">
                    <TrendingUp className="h-5 w-5 text-green-500" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Main Tabs - Show Limits/Disbursement only for non-Hybrid types */}
        <Tabs defaultValue="details" className="space-y-4">
          <TabsList className={`grid w-full max-w-lg ${requirement?.type === "Hybrid" ? "grid-cols-1" : "grid-cols-3"}`}>
            <TabsTrigger value="details" className="text-sm">
              <FileText className="h-4 w-4 mr-1.5" />
              Details
            </TabsTrigger>
            {requirement?.type !== "Hybrid" && requirement?.type !== "Smart EPP" && (
              <>
                <TabsTrigger value="limits" className="text-sm">
                  <Wallet className="h-4 w-4 mr-1.5" />
                  Limits
                  {sanctionedLendersCount > 0 && (
                    <Badge variant="secondary" className="ml-1.5 h-5 px-1.5 text-xs">
                      {sanctionedLendersCount}
                    </Badge>
                  )}
                </TabsTrigger>
                <TabsTrigger value="disbursement" className="text-sm">
                  <Banknote className="h-4 w-4 mr-1.5" />
                  Disbursement
                  {isDisbursementsLoading && (
                    <Badge variant="outline" className="ml-1.5 h-5 px-1.5 text-xs">
                      Loading
                    </Badge>
                  )}
                  {disbursementsCount > 0 && (
                    <Badge variant="secondary" className="ml-1.5 h-5 px-1.5 text-xs">
                      {disbursementsCount}
                    </Badge>
                  )}
                </TabsTrigger>
              </>
            )}
          </TabsList>

          {/* Details Tab */}
          <TabsContent value="details" className="mt-4">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Left Panel - Lender List */}
              <div className="lg:col-span-1">
                <Card className="h-full">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Building2 className="h-5 w-5 text-primary" />
                        <span>Tagged Lenders ({requirement?.lenders?.length || 0})</span>
                      </div>
                      <div>
                        
                      </div>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <ScrollArea className="h-[500px]">
                      {requirement?.lenders?.length === 0 && String(requirement?.status) === "5" ? (
                        <div className="p-6 text-center text-muted-foreground">
                          <Building2 className="h-10 w-10 mx-auto mb-2 opacity-50" />
                          <p className="text-sm">No lenders tagged yet</p>
                          <Button variant="link" size="sm" onClick={() => setAddLenderOpen(true)}>
                            <Plus className="h-4 w-4 mr-1" />
                            Add your first lender
                          </Button>
                        </div>
                      ) : (
                        <div className="divide-y">
                          {requirement.lenders.map((lender:any) => {
                            const financials = getLenderFinancials(lender as any);
                            const statusConfig = getStatusConfig(financials.statusId as any);
                            const StatusIcon = statusConfig?.icon;
                            const isSelected = selectedLenderId === lender.id;

                            return (
                              <div
                                key={lender.id}
                                className={`p-4 cursor-pointer transition-colors hover:bg-muted/50 ${isSelected ? 'bg-primary/5 border-l-2 border-primary' : ''}`}
                                onClick={() => setSelectedLenderId(lender.id)}
                              >
                                <div className="flex items-start gap-3">
                                  {/* <div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center shrink-0 overflow-hidden"> */}
                                    {lender.institutionAvatar ? (
                                       <img
                                       src={
                                         lender?.institutionAvatar.startsWith("http")
                                           ? lender?.institutionAvatar
                                           : `${import.meta.env.VITE_API_BASE_URL}/${lender?.institutionAvatar}`
                                       }
                                       alt={lender?.institutionName}
                                       className="w-[80px] h-[40px] rounded-full object-cover"
                                     />
                                    ) : (
                                      <Building2 className="h-5 w-5 text-muted-foreground" />
                                    )}
                                  {/* </div> */}
                                  <div className="flex-1 min-w-0 flex justify-between items-start gap-2">
                                    <div>
                                      <div className="font-medium truncate">{lender.institutionName}</div>
                                      <div className="text-sm text-muted-foreground truncate">{lender.lenderName}</div>                                   
                                    </div>
                                    <div className="flex items-center gap-2 shrink-0">
                                      {StatusIcon && <StatusIcon className={`h-3.5 w-3.5 ${statusConfig?.color || ''}`} />}
                                      <Badge variant={statusConfig?.variant || 'secondary'} className="text-xs">
                                        {financials.status || 'Deal Shared'}
                                      </Badge>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </ScrollArea>
                  </CardContent>
                </Card>
              </div>

              {/* Right Panel - Lender Details + Requirement Info */}
              <div className="lg:col-span-2 space-y-6">
                {/* Requirement Info */}
                <Card>
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-base">Requirement Information</CardTitle>
                      {/* <div className="flex items-center gap-2">
                        <Label htmlFor="fulfilled-toggle" className="text-sm text-muted-foreground">
                          Requirement Fulfilled
                        </Label>
                        <Switch
                          id="fulfilled-toggle"
                          checked={requirement.fulfilled ?? false}
                          onCheckedChange={(checked) => {
                            updateRequirement({ ...requirement, fulfilled: checked });
                          }}
                        />
                        <span className="text-sm font-medium w-8">
                          {requirement.fulfilled ? "Yes" : "No"}
                        </span>
                      </div> */}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Left: Core basics + Meta */}
                      <div className="space-y-4">
                        <h4 className="text-sm font-medium text-muted-foreground">Basic Details</h4>
                        <div className="grid grid-cols-2 gap-3 text-sm">
                          

                          <div className="flex items-center gap-2 text-muted-foreground">
                            <IndianRupee className="h-4 w-4" />
                            <span>Quantum</span>
                          </div>
                          <div className="font-medium">₹{new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(displayRequirement!.quantum)}</div>

                          <div className="flex items-center gap-2 text-muted-foreground">
                            <Clock className="h-4 w-4" />
                            <span>Tenure</span>
                          </div>
                          <div>{displayRequirement!.tenure} months</div>

                          <div className="flex items-center gap-2 text-muted-foreground">
                            <UserRound className="h-4 w-4" />
                            <span>Application RM</span>
                          </div>
                          <div>{displayRequirement!.applicationRM || "—"}</div>
                          <div className="flex items-center gap-2 text-muted-foreground">
                            <Building2 className="h-4 w-4" />
                            <span>Asset Category</span>
                          </div>
                          <div>{displayRequirement!.assetCategoryName || "—"}</div>
                         
                        </div>
                      </div>

                      {/* Right: Products + Note */}
                      <div className="space-y-4">
                      <h4 className="text-sm font-medium text-muted-foreground">Requirement Details</h4>
                        <div className="grid grid-cols-2 gap-2">

                        <div className="flex items-center gap-2 text-muted-foreground">
                            <Briefcase className="h-4 w-4" />
                            <span>Type</span>
                          </div>
                          <div className="col-span-1"><Badge variant="info">{displayRequirement!.type}</Badge></div>


                          {/* <h4 className="text-sm font-medium text-muted-foreground">Products</h4> */}
                          <div className="flex items-center gap-2 text-muted-foreground">
                            <Package className="h-4 w-4" />
                            <span>Products</span>
                          </div>
                          <div className="flex flex-wrap gap-1.5">
                            <Badge variant="outline">{displayRequirement!.productTypeName || displayRequirement!.type}</Badge>
                          </div>

                          <div className="flex items-center gap-2 text-muted-foreground">
                            <CalendarDays className="h-4 w-4" />
                            <span>Created</span>
                          </div>
                          <div>{new Date(displayRequirement!.createdAt).toLocaleDateString("en-GB")}</div>
                        </div>

                        {displayRequirement!.dealNote && (
                          <div className="pt-2">
                            <div className="text-sm text-muted-foreground mb-1">Note</div>
                            <p className="text-sm">{displayRequirement!.dealNote}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Selected Lender Details */}
                {selectedLender ? (
                  <Card>
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-base flex items-center gap-2">
                          <span className="bg-muted overflow-hidden flex items-center justify-center shrink-0">
                            {selectedLender.institutionAvatar ? (
                              <img
                                src={`${import.meta.env.VITE_API_BASE_URL}/${selectedLender.institutionAvatar}`}
                                alt={selectedLender.institutionName}
                               className="w-[80px] h-[40px] rounded-full object-cover"
                              />
                              
                            ) : (
                              <Building2 className="h-5 w-5 text-primary" />
                            )}
                          </span>
                          {selectedLender.institutionName}
                        </CardTitle>
                        
                        <div className="flex gap-2">
                          {(() => {
                            const lastEntry = selectedLender.statusHistory[selectedLender.statusHistory.length - 1];
                            const latestStatusId = lastEntry != null ? (lastEntry.statusId ?? lastEntry.status) : undefined;
                            const latestStatusStr = String(latestStatusId ?? "");
                            const isLeasing = String(requirement?.type ?? "").toLowerCase() === "leasing";
                            const isSanctioned = latestStatusStr === "8";
                            const isDisbursed = latestStatusStr === "9";
                            // Hide button: Leasing + status 8 (Sanctioned), or non-Leasing + status 9 (Disbursed) — same as RequirementCard
                            const hideUpdateStatus =
                              (isLeasing && isSanctioned) || (!isLeasing && isDisbursed);
                            if (hideUpdateStatus) return null;
                            return (
                              <Button
                                size="sm"
                                onClick={() => setStatusOpenFor(selectedLender?.id ?? null)}
                                title="Update status"
                              >
                                <RefreshCw className="h-4 w-4 mr-1" />
                                Update Status
                              </Button>
                            );
                          })()}
                        </div>
                      </div>
                      <p className="text-sm text-muted-foreground">{selectedLender.lenderName}</p>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Financial Summary */}
                        <div className="space-y-4">
                          <h4 className="text-sm font-medium text-muted-foreground">Financial Summary</h4>
                          {(() => {
                            const financials = getLenderFinancials(selectedLender);
                            const sanctionDate = selectedLender.statusHistory.find((e: any) => e.statusId === 8)?.timestamp;
                            const collateralDetails = selectedLender.statusHistory.find((e: any) => e.statusId === 8)?.collateralDetails;

                            const hasSanctionOrDisbursement =
                              selectedLender.statusHistory.some(
                                (e: any) =>
                                  typeof e.sanctionAmount === "number" ||
                                  typeof e.disbursedAmount === "number"
                              );

                            return (
                              <div className="space-y-3">
                                <div className="grid grid-cols-2 gap-4">
                                  <div className="p-3 rounded-lg bg-blue-500/10">
                                    <div className="text-xs text-muted-foreground">Sanctioned</div>
                                    <div className="text-lg font-semibold">
                                      {typeof financials.sanctioned === 'number' 
                                        ? `₹${new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(financials.sanctioned)}`
                                        : '—'}
                                    </div>
                                  </div>
                                  <div className="p-3 rounded-lg bg-green-500/10">
                                    <div className="text-xs text-muted-foreground">Disbursed</div>
                                    <div className="text-lg font-semibold">
                                      {typeof financials.disbursed === 'number' 
                                        ? `₹${new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(financials.disbursed)}`
                                        : '—'}
                                    </div>
                                  </div>
                                </div>
                                
                                {(financials.customerPayout || financials.lenderPayout || financials.partnerPayout) && (
                                  <div className="p-3 rounded-lg bg-muted/50">
                                    <div className="text-xs text-muted-foreground mb-2">Payout Breakdown</div>
                                    <div className="grid grid-cols-2 gap-2 text-sm">
                                      <div>
                                        <div className="text-xs text-muted-foreground">Customer</div>
                                        <div className="font-medium text-primary">
                                          ₹{new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(financials.customerPayout || 0)}
                                        </div>
                                      </div>
                                      <div>
                                        <div className="text-xs text-muted-foreground">Lender</div>
                                        <div className="font-medium text-primary">
                                          ₹{new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(financials.lenderPayout || 0)}
                                        </div>
                                      </div>
                                      <div>
                                        <div className="text-xs text-muted-foreground">Partner</div>
                                        <div className="font-medium text-destructive">
                                          ₹{new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(financials.partnerPayout || 0)}
                                        </div>
                                      </div>
                                      <div>
                                        <div className="text-xs text-muted-foreground">Net Revenue</div>
                                        <div className="font-medium text-destructive">
                                          ₹{new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format((financials?.customerPayout || 0) + (financials?.lenderPayout || 0) - (financials?.partnerPayout || 0))}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                )}

                                <div className="grid grid-cols-2 gap-2 text-sm">
                                  <div className="text-muted-foreground">Tagged On</div>
                                  <div>{new Date(selectedLender.taggedAt).toLocaleDateString("en-GB")}</div>
                                  <div className="text-muted-foreground">Lender RM</div>
                                  <div>{selectedLender.applicationRM || '—'}</div>
                                  <div className="text-muted-foreground">Sanction Date</div>
                                  <div>{sanctionDate ? new Date(sanctionDate).toLocaleDateString("en-GB") : '—'}</div>
                                </div>

                                <div className="grid grid-cols-1 gap-3 text-sm">

                                {hasSanctionOrDisbursement && (
                                <div className="space-y-2">
                  <div className="font-medium flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    Sanction Letter Attachments
                  </div>

                  {/* ********* */}
                  {sanctionLetterUrl != "" ? (
                    <div
                      key={sanctionLetterUrl}
                      className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 transition-colors"
                    >
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-[hsl(var(--color-admin-primary))]" />
                        <span className="text-sm font-medium">
                          Sanction Letter Attachment
                        </span>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleViewAttachment(sanctionLetterUrl)}
                          className="h-8 px-3 text-xs"
                        >
                          View
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            handleRemoveAttachment(selectedLender?.id ?? "", "sanctionLetterUrl")
                          }
                          className="h-8 px-3 text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="text-sm text-muted-foreground">
                        No attachments
                      </div>

                      {/* Generic upload block (dashed) */}
                      <div className="flex items-center justify-between p-3 border-2 border-dashed rounded-lg hover:bg-muted/50 transition-colors">
                        <div className="flex items-center gap-2">
                          <Upload className="h-4 w-4 text-muted-foreground" />
                          <span className="text-sm text-muted-foreground">
                            Upload Sanction Letter
                          </span>
                        </div>
                        <label className="relative">
                          <input
                            type="file"
                            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                            disabled={!!uploadingDocType || !selectedLender?.id}
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file && selectedLender?.id) {
                                void handleSanctionLetterUpload(selectedLender.id, file);
                                // reset so same file can be chosen again if needed
                                e.target.value = "";
                              }
                            }}
                          />
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            disabled={!!uploadingDocType || !selectedLender?.id}
                            className="h-8 px-3 text-xs pointer-events-none"
                          >
                            {uploadingDocType ? "Uploading..." : "Upload"}
                          </Button>
                        </label>
                      </div>
                    </>
                  )}
                </div>
                                )}

                                  {(selectedLenderSanctionEntry?.sanctionAmount || selectedLenderSanctionEntry?.disbursedAmount) && (
                                    <div className="rounded-lg border bg-muted/30 p-4">
                                      <div className="flex items-center gap-2 mb-3">
                                        <Link className="h-4 w-4 text-primary" />
                                        <div className="text-sm font-medium">Collateral / Security Details</div>
                                      </div>
                                      <Textarea
                                        value={collateralDraft}
                                        onChange={(e) => setCollateralDraft(e.target.value)}
                                        onBlur={() => {
                                          const next = collateralDraft.trim();
                                          const prev = String(collateralDetails ?? "").trim();
                                          if (next === prev) return;
                                          void saveCollateralDetails(next);
                                        }}
                                        placeholder="Enter collateral / security details..."
                                        className="min-h-[80px] bg-background"
                                        disabled={isSavingCollateral}
                                      />
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })()}
                        </div>

                        {/* Status Timeline */}
                        <div className="space-y-4 p-2">
                          <h4 className="text-sm font-medium text-muted-foreground">Status Timeline</h4>
                          <div className="max-h-full overflow-y-auto pr-2">
                            <StatusTimeline entries={selectedLender.statusHistory} adminUserNames={adminUserList} />
                          </div>
                        </div>
                      </div>

                      {/* Notes */}
                      {selectedLender.note && (
                        <div className="mt-4 pt-4 border-t">
                          <div className="text-sm text-muted-foreground mb-1">Notes</div>
                          <p className="text-sm">{selectedLender.note}</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ) : (
                  <Card className="p-8 text-center">
                    <Building2 className="h-12 w-12 mx-auto text-muted-foreground/50 mb-3" />
                    <h3 className="text-lg font-medium mb-1">Select a Lender</h3>
                    <p className="text-sm text-muted-foreground">
                      Click on a lender from the list to view their details
                    </p>
                  </Card>
                )}

               
              </div>
            </div>
          </TabsContent>

          {/* Limits Tab - Only for non-Hybrid */}
          {requirement?.type !== "Hybrid" && requirement?.type !== "Smart EPP" && (
            <TabsContent value="limits" className="mt-4">
              <LimitsTab lenders={requirement?.lenders || []} disbursements={disbursements} />
            </TabsContent>
          )}

          {/* Disbursement Tab - Only for non-Hybrid */}
          {requirement?.type !== "Hybrid" && requirement?.type !== "Smart EPP" && (
            <TabsContent value="disbursement" className="mt-4">
              <DisbursementTab
                disbursements={disbursements}
                lenders={requirement?.lenders || []}
                onAddDisbursement={handleAddDisbursement}
                requirementType={requirement?.type || ""}
              />
            </TabsContent>
          )}
        </Tabs>
      </div>

      {/* Dialogs */}
      <AddLenderDialog admin_user_list={adminUserList} open={addLenderOpen} onOpenChange={setAddLenderOpen} institutions={lenderCompanyList} onSubmit={handleAddLender} />

      <RequirementForm
        open={editReqOpen}
        onOpenChange={(v) => !v && setEditReqOpen(false)}
        requirement={displayRequirement ?? requirement}
        LenderStatus={LenderStatus || []}
        onSubmit={(vals: RequirementFormValues) => handleUpdateRequirement(vals)}
        isSubmitting={isUpdatingRequirement}
        lender_user_product_types={lenderUserProductTypes}
        uniqueRmNames={adminUserList}
      />
      
      {statusOpenFor && (() => {
        const lender = requirement.lenders.find((l) => l.id === statusOpenFor);
        if (!lender) return null;
        const lastSanction = [...lender.statusHistory].reverse().find(e => typeof e.sanctionAmount === 'number')?.sanctionAmount;
        const currentStatus = lender.statusHistory[lender.statusHistory.length - 1]?.statusId;
        return (
          <LenderStatusDialog
            open={!!statusOpenFor}
            onOpenChange={(v) => !v && setStatusOpenFor(null)}
            onSubmit={(vals) => handleStatusUpdate(statusOpenFor, vals)}
            isHybrid={requirement.type === "Hybrid"}
            sanctionedAmountFromHistory={lastSanction}
            lenderInstitution={lender.institutionName ?? (lender as any).institution}
            lenderName={lender.lenderName}
            currentStatus={currentStatus as any}
            LenderStatus={LenderStatus}
            isLeasing={requirement.type === "Leasing" ? true : false}
          />

          
        );
      })()}
    </div>
  );
}
